import {
  useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes,
} from 'react';
import { money, numInput, parseNum } from '../lib/format.ts';
import { iconPaths } from '../lib/icons.tsx';

export function cx(...c: (string | false | null | undefined)[]) {
  return c.filter(Boolean).join(' ');
}

const mobileQuery = typeof window !== 'undefined' ? window.matchMedia('(max-width: 760px)') : null;
/** true below the tablet breakpoint (the tab bar + sheets layout) */
export function useIsMobile() {
  return useSyncExternalStore(
    (cb) => { mobileQuery?.addEventListener('change', cb); return () => mobileQuery?.removeEventListener('change', cb); },
    () => mobileQuery?.matches ?? false,
  );
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' | 'plain'; size?: 'sm' | 'md'; busy?: boolean };
/** busy keeps the button's width: the label fades out and a spinner takes its place */
export function Button({ variant = 'plain', size = 'md', busy, className, children, disabled, ...rest }: BtnProps) {
  return (
    <button type="button" className={cx('btn', `btn-${variant}`, size === 'sm' && 'btn-sm', busy && 'is-busy', className)} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      <span className="btn-label">{children}</span>
      {busy ? <span className="spinner btn-spinner" aria-hidden /> : null}
    </button>
  );
}

/** two-step destructive action: first click arms, second confirms */
export function ConfirmButton({ onConfirm, children, confirmText = 'Точно удалить?', size = 'sm' }: { onConfirm: () => unknown; children: ReactNode; confirmText?: string; size?: 'sm' | 'md' }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3500);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <Button variant={armed ? 'danger' : 'ghost'} size={size} busy={busy}
      onClick={async () => {
        if (!armed) { setArmed(true); return; }
        setBusy(true);
        try { await onConfirm(); } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); setArmed(false); }
      }}>
      {armed ? confirmText : children}
    </Button>
  );
}

const reducedMotion = () => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// the page under open dialogs is pinned in place: iOS scrolls the document to a focused field even with
// overflow: hidden, and then a sheet jumps; position: fixed leaves it only the visual viewport to pan
// the tallest visible area seen at the current width: the screen without a keyboard
let screenWidth = 0;
let screenHeight = 0;

let scrollLocks = 0;
let lockedY = 0;
function lockScroll() {
  if (scrollLocks++) return;
  lockedY = window.scrollY;
  Object.assign(document.body.style, { position: 'fixed', top: `-${lockedY}px`, left: '0', right: '0', overflow: 'hidden' });
}
function unlockScroll() {
  if (--scrollLocks) return;
  Object.assign(document.body.style, { position: '', top: '', left: '', right: '', overflow: '' });
  window.scrollTo({ top: lockedY, behavior: 'instant' });
}

/**
 * Dialog. On phones it rises from the bottom as a sheet; `full` makes it cover the screen (pickers),
 * `sheet` hides the title bar and shows a drag handle (quick add). `head` stays above the scrolling
 * body, `dock` is pinned to the bottom edge over it (the amount keypad).
 * Closing by the user (×, backdrop, Esc) plays the exit before `onClose`.
 */
export function Modal({ title, onClose, children, footer, wide, full, sheet, bodyClass, className, head, dock, onPointerDownCapture }: {
  title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; full?: boolean; sheet?: boolean; bodyClass?: string;
  className?: string; head?: ReactNode; dock?: ReactNode; onPointerDownCapture?: (e: React.PointerEvent) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const pressedBackdrop = useRef(false);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const d = ref.current!;
    d.showModal();
    // showModal moves focus to the first control; the field marked for autofocus gets it back
    d.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    lockScroll();
    return unlockScroll;
  }, []);
  // iOS: the keyboard covers the bottom of the screen and the page pans under it. While it is open the sheet
  // ends at the keyboard's top edge (`.kb-open`: it moves up with the visible area and its height becomes that
  // area's), so every field and «Отмена»/«Сохранить» stay visible; the field being edited scrolls to the middle
  // of the sheet, and dragging the sheet's content puts the keyboard away. Nothing is scrolled before the
  // keyboard opens: moving a field under the finger can make iOS drop the tap, and the keyboard never comes.
  useEffect(() => {
    const d = ref.current;
    const vv = window.visualViewport;
    if (!d || !vv || !mobileQuery?.matches) return;
    const body = d.querySelector<HTMLElement>('.modal-body');
    // a picker opened from this sheet is a dialog inside it: its fields and gestures are its own business
    const own = (el: Element | null) => !!el && el.closest('dialog') === d;
    const field = () => {
      const a = document.activeElement;
      return a instanceof HTMLElement && own(a) && a.matches('input, textarea, select') ? a : null;
    };
    // the field goes to the middle of the part of the body that is above the keyboard (in the sheet's own
    // coordinates: the iOS pan does not move the dialog, so vv.offsetTop is not added here)
    const center = () => {
      const el = field();
      if (!el || !body || keyboard() < 120) return;
      const r = el.getBoundingClientRect();
      const b = body.getBoundingClientRect();
      const top = b.top;
      const bottom = Math.min(b.bottom, vv.height);
      if (bottom - top < 40) return;
      body.scrollBy({ top: r.top + r.height / 2 - (top + bottom) / 2, behavior: 'smooth' });
    };
    let timer = 0;
    const later = () => { window.clearTimeout(timer); timer = window.setTimeout(center, 120); };
    // the keyboard coming or going: the sheet moves and resizes with it instead of jumping (CSS .kb-anim)
    let animTimer = 0;
    const animate = () => {
      if (reducedMotion()) return;
      d.classList.add('kb-anim');
      window.clearTimeout(animTimer);
      animTimer = window.setTimeout(() => d.classList.remove('kb-anim'), 320);
    };
    // the keyboard's height: against the tallest visible area seen at this width, so it is found whether the
    // browser keeps the layout height (Safari) or shrinks it as well
    const keyboard = () => {
      if (window.innerWidth !== screenWidth) { screenWidth = window.innerWidth; screenHeight = 0; }
      screenHeight = Math.max(screenHeight, window.innerHeight, vv.height);
      return Math.max(0, Math.round(screenHeight - vv.height));
    };
    // the sheet's bottom edge goes to the bottom of the visible area: the keyboard's top while it is open
    const follow = () => {
      const kb = keyboard();
      const open = kb > 120;
      if (open !== d.classList.contains('kb-open')) animate();
      d.classList.toggle('kb-open', open);
      d.style.setProperty('--vv-h', `${Math.round(vv.height)}px`);
      // a sheet sits on the bottom edge and moves up by the keyboard; a full-screen picker sits on the top edge and
      // only shrinks. The iOS pan (vv.offsetTop) is left out: the dialog does not travel with it, and adding it put
      // the sheet's top in the middle of the screen with the edited field behind the keyboard
      const lift = open && !d.classList.contains('modal-full') ? Math.min(0, vv.height - window.innerHeight) : 0;
      d.style.setProperty('--vv-shift', `${Math.round(lift)}px`);
      // the strip under the sheet, behind the keyboard that iOS draws translucent, takes the sheet's colour
      d.style.setProperty('--kb', `${kb}px`);
    };
    const resize = () => { follow(); later(); };
    let startY: number | null = null;
    const touchStart = (e: TouchEvent) => { startY = own(e.target as Element) ? e.touches[0]?.clientY ?? null : null; };
    const touchMove = (e: TouchEvent) => {
      const el = field();
      if (el && startY !== null && Math.abs((e.touches[0]?.clientY ?? startY) - startY) > 12) { el.blur(); startY = null; }
    };
    const focusIn = (e: FocusEvent) => { if (own(e.target as Element)) later(); };
    // only the body scrolls: focusing a field must not scroll the sheet itself (its top would slide off screen,
    // the hidden amount keypad below gives it room to); the body brings the field into view instead
    const pin = () => { if (d.scrollTop) d.scrollTop = 0; };
    d.addEventListener('scroll', pin);
    follow();
    vv.addEventListener('resize', resize);
    vv.addEventListener('scroll', follow);
    d.addEventListener('focusin', focusIn);
    body?.addEventListener('touchstart', touchStart, { passive: true });
    body?.addEventListener('touchmove', touchMove, { passive: true });
    return () => {
      d.removeEventListener('scroll', pin);
      window.clearTimeout(timer);
      window.clearTimeout(animTimer);
      vv.removeEventListener('resize', resize);
      vv.removeEventListener('scroll', follow);
      d.removeEventListener('focusin', focusIn);
      body?.removeEventListener('touchstart', touchStart);
      body?.removeEventListener('touchmove', touchMove);
    };
  }, []);
  const close = () => {
    if (leaving) return;
    if (reducedMotion()) { onClose(); return; }
    setLeaving(true);
    setTimeout(onClose, 200);
  };
  // phones: the handle and the title row drag the sheet down; far or fast enough closes it, otherwise it springs back
  const drag = useRef<{ id: number; startY: number; lastY: number; lastT: number; dy: number; v: number } | null>(null);
  const dragStart = (e: React.PointerEvent) => {
    if (full || !mobileQuery?.matches || (e.pointerType === 'mouse' && e.button !== 0)) return;
    if ((e.target as Element).closest('button, a, input, select, textarea')) return;
    // the keyboard goes away first, so the sheet is not dragged under it
    const a = document.activeElement;
    if (a instanceof HTMLElement && ref.current?.contains(a)) a.blur();
    drag.current = { id: e.pointerId, startY: e.clientY, lastY: e.clientY, lastT: e.timeStamp, dy: 0, v: 0 };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the pointer is already gone */ }
  };
  const dragMove = (e: React.PointerEvent) => {
    const d = drag.current, sheetEl = ref.current;
    if (!d || d.id !== e.pointerId || !sheetEl) return;
    if (e.timeStamp > d.lastT) d.v = (e.clientY - d.lastY) / (e.timeStamp - d.lastT);
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    d.dy = Math.max(0, e.clientY - d.startY);
    sheetEl.style.transition = 'none';
    sheetEl.style.transform = `translateY(${d.dy}px)`;
    // the dimmed page behind lightens as the sheet goes down
    sheetEl.style.setProperty('--drag', String(Math.min(1, d.dy / Math.max(1, sheetEl.offsetHeight))));
  };
  const dragEnd = (e: React.PointerEvent) => {
    const d = drag.current, sheetEl = ref.current;
    if (!d || d.id !== e.pointerId || !sheetEl) return;
    drag.current = null;
    const far = d.dy > Math.min(160, sheetEl.offsetHeight * 0.25), fast = d.dy > 30 && d.v > 0.6;
    // closing goes on from where the finger left the sheet (the exit animation starts at the current offset)
    if (e.type === 'pointerup' && (far || fast)) { sheetEl.style.transition = ''; close(); return; }
    sheetEl.style.transition = 'transform var(--m-sheet) var(--ease-out)';
    sheetEl.style.transform = '';
    sheetEl.style.removeProperty('--drag');
  };
  const grab = { onPointerDown: dragStart, onPointerMove: dragMove, onPointerUp: dragEnd, onPointerCancel: dragEnd };
  return (
    <dialog ref={ref} className={cx('modal', wide && 'modal-wide', full && 'modal-full', sheet && 'modal-sheet', leaving && 'leaving', className)} aria-label={typeof title === 'string' ? title : undefined}
      onCancel={(e) => { e.preventDefault(); close(); }}
      // a press that began inside (selecting a field's text) and ended on the backdrop is not a backdrop click
      onPointerDown={(e) => { pressedBackdrop.current = e.target === ref.current; }}
      onClick={(e) => { if (e.target === ref.current && pressedBackdrop.current) close(); }}>
      <div className="modal-inner" onPointerDownCapture={onPointerDownCapture}>
        <span className="sheet-handle" aria-hidden {...grab} />
        <header className="modal-head" {...grab}>
          {sheet ? <span className="muted small">{title}</span> : <h2>{title}</h2>}
          <button type="button" className="icon-btn" onClick={close} aria-label="Закрыть"><Icon name="close" /></button>
        </header>
        {head}
        <div className={cx('modal-body', bodyClass)}>{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
        {dock}
      </div>
    </dialog>
  );
}

export function Field({ label, hint, children, className }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={cx('field', className)}>
      <span className="field-label">{label}</span>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Input(props: React.ComponentProps<'input'>) {
  // on a phone a field never takes focus by itself: the keyboard would pop up and push the sheet around
  const autoFocus = !!props.autoFocus && !mobileQuery?.matches;
  return <input className="input" {...props} autoFocus={autoFocus} data-autofocus={autoFocus || undefined} />;
}

export function Select({ children, className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx('input', className)} {...props}>{children}</select>;
}

/** number input accepting «1 234,56»; reports null when empty */
export function NumberInput({ value, onChange, className, ...rest }: { value: number | null | undefined; onChange: (v: number | null) => void } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [text, setText] = useState(numInput(value));
  const last = useRef(value);
  useEffect(() => {
    if (value !== last.current) { setText(numInput(value)); last.current = value; }
  }, [value]);
  return (
    <input className={cx('input num', className)} inputMode="decimal" value={text} {...rest}
      autoFocus={!!rest.autoFocus && !mobileQuery?.matches} data-autofocus={(!!rest.autoFocus && !mobileQuery?.matches) || undefined}
      onChange={(e) => {
        setText(e.target.value);
        const n = parseNum(e.target.value);
        last.current = n;
        onChange(n);
      }} />
  );
}

export function Money({ value, currency = 'RUB', sign, className }: { value: number | null | undefined; currency?: string; sign?: boolean; className?: string }) {
  const neg = (value ?? 0) < 0;
  return <span className={cx('money', sign && (neg ? 'neg' : (value ?? 0) > 0 ? 'pos' : ''), className)}>{money(value, currency, { sign })}</span>;
}

export function Progress({ value, tone = 'ink', label }: { value: number; tone?: 'ink' | 'rose' | 'sky' | 'sage' | 'debt' | 'amber' | 'accent'; label?: string }) {
  const v = Math.max(0, Math.min(1, value));
  return (
    <div className={cx('progress', `tone-${tone}`)} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)} aria-label={label}>
      <div className="progress-fill" style={{ width: `${v * 100}%` }} />
    </div>
  );
}

/** icon tile for categories and wallets: tint derived from the item colour */
export function Tile({ icon, color, size, label, image }: { icon: string; color: string; size?: 'sm' | 'lg'; label?: string; image?: string | null }) {
  // a category's own picture fills the circle instead of the drawn icon
  if (image) {
    return (
      <span className={cx('tile tile-image', size && `tile-${size}`)} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
        <img src={image} alt="" draggable={false} />
      </span>
    );
  }
  const style = { '--tile-bg': `color-mix(in srgb, ${color} 18%, transparent)`, '--tile-fg': color } as React.CSSProperties;
  return (
    <span className={cx('tile', size && `tile-${size}`)} style={style} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <Icon name={icon} size={size === 'sm' ? 15 : size === 'lg' ? 22 : 18} />
    </span>
  );
}

/** the period ruler in miniature: three ledger lines of falling length */
export function BrandMark() {
  return <span className="brand-mark" aria-hidden><i /><i /><i /></span>;
}

export function Loading({ label = 'Загружаю…' }: { label?: string }) {
  return <div className="loading"><span className="spinner" aria-hidden /> {label}</div>;
}

export function ErrorBox({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <div className="error-box" role="alert">
      <span>{error.message}</span>
      {onRetry ? <Button size="sm" onClick={onRetry}>Повторить</Button> : null}
    </div>
  );
}

export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return <div className="empty"><p>{children}</p>{action}</div>;
}

/** one indicator slides to the chosen option; arrow keys move the choice */
export function Segmented<T extends string>({ value, onChange, options, label, wide }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[]; label: string; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const place = () => {
      const cur = el.querySelector<HTMLElement>('button[aria-selected="true"]');
      if (!cur) return;
      el.style.setProperty('--seg-x', `${cur.offsetLeft}px`);
      el.style.setProperty('--seg-w', `${cur.offsetWidth}px`);
    };
    place();
    // the first placement must not slide in from the left edge
    if (!placed) requestAnimationFrame(() => setPlaced(true));
    const ro = new ResizeObserver(place);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value, options.length]);
  const move = (d: number) => {
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + d + options.length) % options.length];
    onChange(next.value);
    requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('button[aria-selected="true"]')?.focus());
  };
  return (
    <div ref={ref} className={cx('segmented', wide && 'segmented-wide', placed && 'placed')} role="tablist" aria-label={label}
      onKeyDown={(e) => { if (e.key === 'ArrowRight') { e.preventDefault(); move(1); } else if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); } }}>
      <span className="seg-indicator" aria-hidden />
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={o.value === value} tabIndex={o.value === value ? 0 : -1} className={cx(o.value === value && 'on')} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function PageHead({ title, sub, kicker, actions }: { title: ReactNode; sub?: ReactNode; kicker?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        {kicker ? <div className="page-kicker">{kicker}</div> : null}
        <h1>{title}</h1>
        {sub ? <p className="page-sub">{sub}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function Section({ title, aside, children, className, tone }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string; tone?: string }) {
  const id = useId();
  return (
    <section className={cx('section', tone && `section-${tone}`, className)} aria-labelledby={title ? id : undefined}>
      {title || aside ? (
        <div className="section-head">
          {title ? <h2 id={id}>{title}</h2> : <span />}
          {aside ? <div className="section-aside">{aside}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** small action menu (⋯) */
export function Menu({ label, items }: { label: string; items: { label: ReactNode; icon?: string; onClick: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // a menu low on the screen opens upward when it would go under the tab bar or past the visible area
  useLayoutEffect(() => {
    const list = ref.current?.querySelector<HTMLElement>('.menu-list');
    const trigger = ref.current?.firstElementChild;
    if (!open || !list || !trigger) { setUp(false); return; }
    const vv = window.visualViewport;
    const top = vv ? vv.offsetTop : 0;
    let bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const bar = document.querySelector<HTMLElement>('.tabbar');
    if (bar && getComputedStyle(bar).display !== 'none') bottom = Math.min(bottom, bar.getBoundingClientRect().top);
    const t = trigger.getBoundingClientRect();
    const below = bottom - t.bottom - 8, above = t.top - top - 8;
    setUp(list.offsetHeight > below && above > below);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="menu" ref={ref}>
      <button type="button" className="icon-btn" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}><Icon name="more" /></button>
      {open ? (
        <div className={cx('menu-list', up && 'menu-up')} role="menu">
          {items.map((it, i) => (
            <button key={i} type="button" role="menuitem" className={cx('menu-item', it.danger && 'neg')} onClick={() => { setOpen(false); it.onClick(); }}>
              {it.icon ? <Icon name={it.icon} size={18} /> : null}{it.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------- toasts ----------
type Toast = { id: number; text: string; tone: 'ok' | 'error' };
let toasts: Toast[] = [];
const toastSubs = new Set<() => void>();
let toastId = 0;
export function toast(text: string, tone: Toast['tone'] = 'ok') {
  const t = { id: ++toastId, text, tone };
  toasts = [...toasts, t];
  toastSubs.forEach((s) => s());
  setTimeout(() => { toasts = toasts.filter((x) => x.id !== t.id); toastSubs.forEach((s) => s()); }, tone === 'error' ? 6000 : 2800);
}
export function Toasts() {
  const list = useSyncExternalStore((cb) => { toastSubs.add(cb); return () => { toastSubs.delete(cb); }; }, () => toasts);
  return (
    <div className="toasts" aria-live="polite">
      {list.map((t) => <div key={t.id} className={cx('toast', t.tone === 'error' && 'toast-error')}>{t.text}</div>)}
    </div>
  );
}

/** run an async mutation with busy state and error toast */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const run = async <T,>(fn: () => Promise<T>, okText?: string): Promise<T | undefined> => {
    setBusy(true);
    try {
      const r = await fn();
      if (okText) toast(okText);
      return r;
    } catch (e) {
      toast((e as Error).message, 'error');
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, run };
}

// ---------- icons: UI set on the same 24px grid as the category icons ----------
const uiPaths: Record<string, string> = {
  overview: 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  budget: 'M4 6h16M4 12h10M4 18h7M18 14v6M15 17h6',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  debt: 'M19 5L5 19M7 9.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zm10 10a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  plan: 'M3 17l6-6 4 4 8-8M14 7h7v7',
  chart: 'M5 20V10M12 20V4M19 20v-7',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.2 2.2M16.9 16.9l2.2 2.2M4.9 19.1l2.2-2.2M16.9 7.1l2.2-2.2',
  plus: 'M12 5v14M5 12h14',
  mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM19 11a7 7 0 0 1-14 0M12 18v3',
  stop: 'M7 7h10v10H7z',
  redo: 'M4 12a8 8 0 1 0 2.3-5.6L4 8.7M4 4v4.7h4.7',
  'swap-v': 'M8 20V4M4 8l4-4 4 4M16 4v16M12 16l4 4 4-4',
  close: 'M6 6l12 12M18 6L6 18',
  left: 'M15 6l-6 6 6 6',
  right: 'M9 6l6 6-6 6',
  down: 'M6 9l6 6 6-6',
  up: 'M18 15l-6-6-6 6',
  grip: 'M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01',
  edit: 'M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3zM13 7l3 3',
  check: 'M5 12l5 5 9-10',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  logout: 'M10 17l5-5-5-5M15 12H3M13 3h6a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-6',
  link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  search: 'M21 21l-4.3-4.3M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z',
  backspace: 'M21 6H9l-6 6 6 6h12a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1zM14 9l-4 6M10 9l4 6',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  'eye-off': 'M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 5.1A10 10 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4.2M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a10 10 0 0 0 4.4-1',
  pin: 'M12 17v5M5 10l2-2V4h10v4l2 2v3H5z',
  calendar: 'M3 5h18v16H3zM3 10h18M8 3v4M16 3v4',
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  sliders: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  bulb: 'M9 18h6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.3 1.1 2.2h5c0-.9.4-1.6 1.1-2.2A6 6 0 0 0 12 3z',
};
export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={uiPaths[name] ?? iconPaths[name] ?? ''} />
    </svg>
  );
}
